import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useOrgs } from './OrgsContext';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface FeatureFlags {
}

const DEFAULT_FLAGS: FeatureFlags = {
};

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  setFlag: (key: keyof FeatureFlags, value: boolean) => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

/**
 * Returns the localStorage key for feature flags scoped to an organization.
 */
function getStorageKey(orgId: string): string {
  return `datamission_${orgId}_feature_flags`;
}

/**
 * Loads feature flags from localStorage for the given organization.
 */
function loadFlags(orgId: string): FeatureFlags {
  try {
    const stored = localStorage.getItem(getStorageKey(orgId));
    if (stored) {
      return { ...DEFAULT_FLAGS, ...JSON.parse(stored) as Partial<FeatureFlags> };
    }
  } catch {
    // Ignore parse errors
  }
  return { ...DEFAULT_FLAGS };
}

/**
 * Provider that manages feature flags persisted in localStorage per organization.
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { selectedOrganizationId } = useOrgs();
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);

  // Reload flags when org changes
  useEffect(() => {
    if (selectedOrganizationId) {
      setFlags(loadFlags(selectedOrganizationId));
    } else {
      setFlags({ ...DEFAULT_FLAGS });
    }
  }, [selectedOrganizationId]);

  const setFlag = useCallback((key: keyof FeatureFlags, value: boolean) => {
    setFlags(prev => {
      const next = { ...prev, [key]: value };
      if (selectedOrganizationId) {
        localStorage.setItem(getStorageKey(selectedOrganizationId), JSON.stringify(next));
      }
      return next;
    });
  }, [selectedOrganizationId]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, setFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

/**
 * Hook to access feature flags.
 * @lintignore — kept as infrastructure for future flags
 */
export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    // eslint-disable-next-line no-restricted-syntax
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
}
